"use client"

import { useEffect, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"

interface LoadingScreenProps {
  onComplete?: () => void
  minDuration?: number // Minimum display time in ms
}

export function LoadingScreen({ onComplete, minDuration = 2000 }: LoadingScreenProps) {
  const [isLoading, setIsLoading] = useState(true)
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    // Simulate loading progress
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval)
          return 100
        }
        // Ease out progress - faster at start, slower at end
        const increment = Math.max(1, (100 - prev) * 0.1)
        return Math.min(100, prev + increment)
      })
    }, 50)

    // Minimum display time
    const timer = setTimeout(() => {
      setIsLoading(false)
      onComplete?.()
    }, minDuration)

    return () => {
      clearInterval(interval)
      clearTimeout(timer)
    }
  }, [minDuration, onComplete])

  return (
    <AnimatePresence>
      {isLoading && (
        <motion.div
          className="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-[var(--color-bg-primary)]"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Brand Logo Animation */}
          <motion.div
            className="relative mb-12"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Animated rings */}
            <motion.div
              className="absolute inset-0 w-24 h-24 border border-[var(--color-accent-gold)]/20 rounded-full"
              animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
              className="absolute inset-0 w-24 h-24 border border-[var(--color-accent-teal)]/20 rounded-full"
              animate={{ scale: [1, 1.8, 1], opacity: [0.3, 0, 0.3] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
            />

            {/* Central mark */}
            <div className="w-24 h-24 flex items-center justify-center">
              <motion.span
                className="text-4xl font-bold text-[var(--color-accent-gold)]"
                style={{ fontFamily: "var(--font-display)" }}
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              >
                R
              </motion.span>
            </div>
          </motion.div>

          {/* Progress bar */}
          <div className="w-48 h-px bg-[var(--color-bg-tertiary)] overflow-hidden rounded-full">
            <motion.div
              className="h-full bg-gradient-to-r from-[var(--color-accent-gold)] to-[var(--color-accent-teal)]"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.1 }}
            />
          </div>

          {/* Loading text */}
          <motion.p
            className="mt-6 text-xs tracking-[0.3em] uppercase text-[var(--color-text-muted)]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Loading Experience
          </motion.p>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
